import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { I18n } from 'react-redux-i18n';
import { useSearchParams } from 'react-router-dom';

import { useSession } from '../../../../hooks/useSession';
import { I18N_BASE } from '../../constants';
import {
  DEFAULT_QUEUE_FILTERS,
  entityScopeFromFilters,
  QueueFilters,
} from '../../models';
import { useReviewQueue } from '../../hooks/useReviewQueue';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux/useApp';
import { queueFiltersSet } from '../../store/contentModerationActions';
import { selectQueueFilters } from '../../store/contentModerationSelectors';
import {
  filtersFromUrlSearchParams,
  filtersToUrlSearchParams,
  urlSearchParamsEqual,
} from './queueUrlState';
import QueueCardList from './QueueCardList';
import QueueFiltersBar from './QueueFiltersBar';
import QueueHistoryHeader from './QueueHistoryHeader';
import RefreshIndicator from './RefreshIndicator';
import styles from './queue.module.css';

export function QueueScreen(): React.ReactElement {
  const dispatch = useAppDispatch();
  const session = useSession();
  const storedFilters = useAppSelector(selectQueueFilters);
  const [searchParams, setSearchParams] = useSearchParams();
  const hydratedFromUrlRef = useRef(false);

  // On first mount only, hydrate Redux filters from URL search params (so
  // a shared/refreshed URL restores the same view).
  useEffect(() => {
    if (hydratedFromUrlRef.current) return;
    hydratedFromUrlRef.current = true;
    const urlFilters = filtersFromUrlSearchParams(searchParams, storedFilters ?? DEFAULT_QUEUE_FILTERS);
    dispatch(queueFiltersSet(urlFilters));
  }, [dispatch, searchParams, storedFilters]);

  // Until the hydration effect above has dispatched, Redux still holds the
  // default filters — using them here would fire a useless first fetch with
  // the wrong scope (e.g. landing on `?fundraiserId=9789` would briefly fetch
  // the default work-pile, then refetch the entity history; the responses can
  // race). Read from the URL synchronously on the first render so the very
  // first fetch matches the URL the user landed on.
  const filters: QueueFilters = useMemo(
    () =>
      hydratedFromUrlRef.current
        ? storedFilters ?? DEFAULT_QUEUE_FILTERS
        : filtersFromUrlSearchParams(searchParams, storedFilters ?? DEFAULT_QUEUE_FILTERS),
    [storedFilters, searchParams],
  );

  // Mirror Redux filters back into the URL so they survive refresh + are
  // shareable. Skip the write if the URL already matches to avoid loops.
  useEffect(() => {
    if (!hydratedFromUrlRef.current) return;
    const nextParams = filtersToUrlSearchParams(filters);
    if (!urlSearchParamsEqual(searchParams, nextParams)) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [filters, searchParams, setSearchParams]);

  const handleFiltersChange = useCallback(
    (next: QueueFilters) => {
      dispatch(queueFiltersSet(next));
    },
    [dispatch],
  );

  const {
    items,
    totalCount,
    hasMore,
    isLoading,
    isPolling,
    isAppending,
    error,
    lastFetchedAt,
    secondsUntilRefresh,
    refresh,
    loadMore,
  } = useReviewQueue(filters);

  // Clearing entity scope returns the screen to the default work-pile view.
  const handleClearScope = useCallback(() => {
    dispatch(queueFiltersSet(DEFAULT_QUEUE_FILTERS));
  }, [dispatch]);

  const currentReviewerAccountId = (session?.account?.id as number | undefined) ?? null;
  const scope = entityScopeFromFilters(filters);

  return (
    <div className={styles.screen} data-testid="content-moderation-queue">
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{I18n.t(`${I18N_BASE}.queue.title`)}</h1>
          <div className={styles.subtitle}>
            {/* "Showing X of N" — X is what the API returned on this page,
                N is the totalCount echoed by the API. When the queue is
                empty we suppress the count and let EmptyState carry the
                messaging. */}
            {totalCount > 0
              ? I18n.t(`${I18N_BASE}.queue.subtitle`, {
                  shown: items.length,
                  total: totalCount,
                })
              : null}
          </div>
        </div>
        <RefreshIndicator
          lastFetchedAt={lastFetchedAt}
          secondsUntilRefresh={secondsUntilRefresh}
          isPolling={isPolling}
          onRefresh={refresh}
          testId="queue-refresh"
        />
      </header>

      {scope ? (
        <QueueHistoryHeader
          scope={scope}
          onBack={handleClearScope}
          testId="queue-history-header"
        />
      ) : (
        <QueueFiltersBar value={filters} onChange={handleFiltersChange} testId="queue-filters" />
      )}

      <QueueCardList
        items={items}
        sort={filters.sort}
        currentReviewerAccountId={currentReviewerAccountId}
        isLoading={isLoading}
        error={error}
        onRetry={refresh}
        hasMore={hasMore}
        isAppending={isAppending}
        onLoadMore={loadMore}
        testId="queue-list"
      />
    </div>
  );
}

export default QueueScreen;
